/**
 * Chat send builder — the boundary between chat composers
 * (`realtime-chat.tsx`, `OrderChatPreview.tsx`) and the HTTP route
 * `app/api/chat/send/route.ts`.
 *
 *   components/realtime-chat.tsx           ┐
 *   components/orders/OrderChatPreview.tsx ┴── intent ──▶ sendChatMessage
 *                                                              │
 *                                                              ├── safeParse (boundary)
 *                                                              └── fetch  ─── 2xx ──▶ SendChatMessageResult
 *                                                                          │
 *                                                                          └── !ok ──▶ throws ChatSendError(status, serverError)
 *
 * Why this exists:
 *  - The contract schema `ChatSendRequest` already shipped (Sibling-A P1,
 *    2026-06-09); the route consumes it; mobile builds its body against the
 *    interface (D2 pin). The seven inline web producers (realtime-chat ×5 +
 *    OrderChatPreview ×2) were the missing piece — each one hand-assembled
 *    a body literal with its own `satisfies ChatSendRequest` and called
 *    `fetch` inline. Sibling-E batch 1 (`.tondocs/RELIABILITY_ROADMAP.md §3a`)
 *    consolidates them into one canonical builder so a Walking Skeleton can
 *    drive producer + consumer in one call.
 *  - The mobile sibling (`apps/mobile/lib/api-messaging.ts::sendChatMessage`)
 *    has shipped this shape for months — typed result + thrown `ChatSendError`
 *    carrying `status`. The web builder is a direct mirror so the
 *    cross-platform behaviour aligns.
 *
 * Design notes:
 *  - Spread-omit-not-null body assembly (mirror mobile): a key is set only
 *    when the caller supplied a non-empty value. Keeps the wire body
 *    byte-equivalent to the legacy inline literals for every code path.
 *  - On `!res.ok` the builder throws `ChatSendError(message, status, serverError)`.
 *    The `serverError` field carries the raw `error` string from the response
 *    payload (or `null` if unreadable) so the delivery layer can run its
 *    moderation / 422-specific UI mapping (toast, setComposerError, throw
 *    `ModerationError`, requeue draft). That mapping deliberately STAYS in
 *    the component — `lib/` must not import `toast` / `setState`.
 *  - Response shape is NOT yet pinned by Zod (same reasoning as
 *    `lib/offers/offer-builders.ts`). The exported `SendChatMessageResult`
 *    type mirrors mobile's hand-rolled shape and the legacy web call-site
 *    contract; tightening it is an explicit out-of-scope follow-up.
 *
 * @see app/api/chat/send/route.ts — handler with boundary safeParse
 * @see apps/mobile/lib/api-messaging.ts sendChatMessage — mobile sibling
 *      (intent, ChatSendError, SendChatMessageResult are zeroed-in mirrors)
 * @see packages/contract/src/chat-send.ts — ChatSendRequestSchema
 * @see lib/chat/chat-upsert.ts — sibling builder (Sibling-E B1)
 * @see lib/offers/offer-builders.ts — pattern reference
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E batch 1 (B2)
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 */

import {
  ChatSendRequestSchema,
  seal,
  type ChatSendRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"
import { KRAAHL_CLIENT_HEADER } from "@/lib/chat/chat-client-source"

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the assembled body fails the contract Zod parse before fetch.
 * Programmer-bug signal: caller passed an intent the contract refuses. The
 * server would 400 anyway; this surfaces it at the responsible line.
 */
export class ChatSendContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `[chat/send] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "ChatSendContractError"
    this.issues = issues
  }
}

/**
 * Thrown when `/api/chat/send` returns a non-2xx response. The delivery
 * layer reads `status` (e.g. 422 → ModerationError + requeue draft) and
 * `serverError` (the raw `error` string from the payload) to produce its
 * user-facing message. `message` is best-effort identical to the legacy
 * inline error string (`payload.error` if present, else `Send (<status>)`).
 *
 * Mirror of `apps/mobile/lib/api-messaging.ts::ChatSendError` with one
 * addition: `serverError` is broken out so the moderation display helper
 * (`getModerationDisplayMessage`) can read the raw server string instead of
 * the formatted Error message.
 */
export class ChatSendError extends Error {
  readonly status: number
  readonly serverError: string | null
  constructor(message: string, status: number, serverError: string | null) {
    super(message)
    this.name = "ChatSendError"
    this.status = status
    this.serverError = serverError
  }
}

// ---------------------------------------------------------------------------
// Intent + result types
// ---------------------------------------------------------------------------

/**
 * Mirror of `apps/mobile/lib/api-messaging.ts::ChatListingPayload` for type
 * symmetry. The wire schema (`ChatSendRequestSchema`) keeps `listing` loose
 * (`passthrough`) — every display field the server forwards to `notify()` is
 * accepted. Local typing is permissive on purpose.
 */
export type ChatListingPayload = NonNullable<ChatSendRequest["listing"]>
export type ChatOrderPayload = NonNullable<ChatSendRequest["order"]>

export interface SendChatMessageIntent {
  /** Canonical (sorted) room name. The route also re-sorts server-side. */
  roomName: string
  /** Message text. Required when no image / listing / order is attached. */
  content?: string
  /** Image URL string. Optional. */
  imageUrl?: string
  /** Listing card metadata. Optional. */
  listing?: ChatListingPayload
  /** Order card metadata. Optional. */
  order?: ChatOrderPayload
  /** Server-attachment id (image upload pointer). Optional. */
  attachmentId?: string
  /** Skip first-inquiry email when `listing` is present. Optional. */
  skipListingEmail?: boolean
  /** Parent message id for threaded replies. Optional. */
  replyToMessageId?: string
}

/**
 * Mirror of mobile's `SendChatMessageResult`. The server returns
 * `{ message: <row> }`; the builder unwraps so callers receive the row
 * directly (same as mobile).
 */
export interface SendChatMessageResult {
  id: string
  content: string
  created_at: string
  sender_id?: string
  image_url?: string | null
  listing_id?: string | null
  order_id?: string | null
  seller_order_id?: string | null
  attachment_id?: string | null
  listing?: ChatListingPayload | null
  order?: ChatOrderPayload | null
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Build, safeParse, POST `/api/chat/send`, and return the `message` row on
 * success. Throws `ChatSendError(message, status, serverError)` on any
 * non-2xx — the delivery layer in the component is expected to map it to
 * user-facing UI (toast / setComposerError / ModerationError on 422 /
 * requeue draft on transport failure).
 */
export async function sendChatMessage(
  intent: SendChatMessageIntent,
): Promise<SendChatMessageResult> {
  const body = {
    roomName: intent.roomName,
    ...(intent.content !== undefined ? { content: intent.content } : {}),
    ...(intent.imageUrl ? { imageUrl: intent.imageUrl } : {}),
    ...(intent.listing ? { listing: intent.listing } : {}),
    ...(intent.order ? { order: intent.order } : {}),
    ...(intent.attachmentId ? { attachmentId: intent.attachmentId } : {}),
    ...(intent.skipListingEmail !== undefined
      ? { skipListingEmail: intent.skipListingEmail }
      : {}),
    ...(intent.replyToMessageId ? { replyToMessageId: intent.replyToMessageId } : {}),
  } satisfies ChatSendRequest

  const parsed = ChatSendRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new ChatSendContractError(
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  // Sibling-E §4 #3c: `seal` after safeParse converts the body into a
  // `Built<ChatSendRequest>`. `postJson` accepts ONLY `Built<T>` — a
  // hand-rolled literal in a component cannot reach this seam.
  const res = await postJson("/api/chat/send", seal<ChatSendRequest>(body), {
    headers: { [KRAAHL_CLIENT_HEADER]: "web" },
  })

  if (!res.ok) {
    const errPayload = await res.json().catch(() => null)
    const serverError =
      errPayload && typeof (errPayload as { error?: unknown }).error === "string"
        ? ((errPayload as { error: string }).error)
        : null
    const message = serverError ?? `Send (${res.status})`
    throw new ChatSendError(message, res.status, serverError)
  }

  const json = (await res.json()) as { message: SendChatMessageResult }
  return json.message
}
